
import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  Alert,
  Modal,
  TextField,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { sendResetPasswordLink } from "../../redux/usersSlice";
import Captcha from "./Captcha";
import backgroundImage from "../../assets/icons/twoFactorBackground.png";

/* ────────────────────────────────────────────────────────────────────────── */
/*  🎨  Design tokens                                                        */
/* ────────────────────────────────────────────────────────────────────────── */
const NAVY       = "#2F4E8B";
const NAVY_LIGHT = "#2F4E8B";
const OUTLINE    = "#D4D9E0";
const LABEL_GREY = "#4F5565";

/* ───  TextField (style « standard ») harmonisé ─── */
const CustomTextField = styled(TextField)(() => ({
  "& .MuiInput-underline:before":  { borderBottomColor: OUTLINE },
  "& .MuiInput-underline:hover:before": { borderBottomColor: NAVY_LIGHT },
  "& .MuiInput-underline:after":  { borderBottomColor: NAVY },
  "& .MuiFormLabel-root":         { color: LABEL_GREY, fontSize: 14 },
  "& .MuiFormLabel-root.Mui-focused": { color: NAVY },
  "& .MuiInputBase-input":        { fontSize: 16, padding: "6px 0" },
}));

/* ───  Scrollbar identique à celui de LandingPage.jsx ─── */
const scrollbar = {
  "&::-webkit-scrollbar":       { width: 8 },
  "&::-webkit-scrollbar-track": { background: "#e6e8f0", borderRadius: 4 },
  "&::-webkit-scrollbar-thumb": { background: NAVY, borderRadius: 4 },
  scrollbarWidth: "thin",
  scrollbarColor: `${NAVY} #e6e8f0`,
};

const SendResetLink = () => {
  const [email, setEmail]             = useState("");
  const [error, setError]             = useState(null);
  const [success, setSuccess]         = useState(null);
  const [loading, setLoading]         = useState(false);
  const [captchaOpen, setCaptchaOpen] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  /* ───────────────  Callback Captcha  ─────────────── */
  const handleCaptchaSuccess = (captchaVerification) => {
    setCaptchaOpen(false);
    setLoading(true);
    setError(null);
    setSuccess(null);

    dispatch(sendResetPasswordLink({ email, captchaVerification }))
      .unwrap()
      .then(() =>
        setSuccess("Lien de réinitialisation envoyé avec succès.")
      )
      .catch(() =>
        setError(
          "Échec de l'envoi du lien de réinitialisation. Adresse introuvable."
        )
      )
      .finally(() => setLoading(false));
  };

  /* ─────────────────────────────────────────────────────────────────────── */
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundImage: `url(${backgroundImage})`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        backgroundPosition: "center",
        px: 2,
      }}
    >
      {/* Carte centrale – plus large (+ paddings) */}
      <Paper
        elevation={8}
        sx={{
          position: "relative",
          borderRadius: 4,
          width: "100%",
          maxWidth: { xs: 500, sm: 640, md: 780 },
          p: { xs: 5, sm: 6, md: 7 },
          bgcolor: "common.white",
        }}
      >
        {/* Flèche retour */}
        <ArrowBackIosNewIcon
          onClick={() => navigate("/")}
          sx={{
            position: "absolute",
            top: 28,
            left: 28,
            fontSize: 28,
            cursor: "pointer",
            color: NAVY,
            "&:hover": { color: NAVY_LIGHT },
          }}
        />

        <Typography
          variant="h4"
          align="center"
          sx={{ fontWeight: 700, color: NAVY, mb: 2 }}
        >
          Réinitialiser votre mot de passe
        </Typography>

        <Typography
          variant="body2"
          align="center"
          sx={{ maxWidth: 420, mx: "auto", color: "#6b6b6b", mb: 4 }}
        >
          Saisissez votre adresse email ci-dessous&nbsp;: nous vous enverrons un
          lien de réinitialisation.
        </Typography>

        {/* Formulaire */}
        <Box
          component="form"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
            maxWidth: 400,
            mx: "auto",
          }}
        >
          <CustomTextField
            label="Adresse email"
            variant="standard"
            fullWidth
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ maxWidth: 350, mx: "auto" }}
          />

          <Button
            variant="contained"
            onClick={() => setCaptchaOpen(true)}
            disabled={loading}
            sx={{
              height: 48,
              borderRadius: 30,
              textTransform: "none",
              fontWeight: "bold",
              bgcolor: NAVY,
              maxWidth: 200,
              mx: "auto",
              "&:hover": { bgcolor: NAVY_LIGHT },
            }}
          >
            {loading ? "Envoi en cours..." : "Envoyer le lien"}
          </Button>

          {error && (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ borderRadius: 2 }}>
              {success}
            </Alert>
          )}
        </Box>

        <Typography
          variant="body2"
          align="center"
          sx={{ color: "#999999", mt: 6 }}
        >
          © 2025 Tallentum. Tous droits réservés.
        </Typography>
      </Paper>

      {/* Modal Captcha – identique à celui de LandingPage.jsx */}
      <Modal
        open={captchaOpen}
        onClose={() => {}}
        BackdropProps={{ onClick: (e) => e.stopPropagation() }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", sm: "90%", md: "80%" },
            height: { xs: "95vh", sm: "90vh" },
            overflowY: "auto",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            border: `2px solid ${NAVY}`,
            ...scrollbar,
          }}
        >
          <Captcha
            onSuccess={handleCaptchaSuccess}
            handleClose={() => setCaptchaOpen(false)}
          />
        </Box>
      </Modal>
    </Box>
  );
};

export default SendResetLink;
